import { Injectable, inject } from '@angular/core';

import { OasisConfigService } from './oasis-config.service';

/**
 * Whether the applicant portal has opened for the cycle, and the copy that tells
 * an early visitor when it will.
 *
 * ## Why a timestamp and not a boolean
 *
 * A boolean flag would need flipping at the exact moment the window opens; a
 * timestamp opens itself. It also keeps the gate and the message derived from ONE
 * value, so the date an applicant reads can never disagree with the date the button
 * appears.
 *
 * ## This is a courtesy, not enforcement
 *
 * The real gate is server-side: `evaluateSaveAuthorization` refuses saves outside
 * `[submissionOpens, submissionDeadline)` and returns `WINDOW_CLOSED`. This only
 * decides what the UI offers.
 *
 * The opening timestamp is served by the public config endpoint, so it is the same
 * `submissionOpens` the server enforces against — the two cannot drift, which is
 * what the previous per-environment `portalOpensAt` constant could not guarantee.
 */

/** DOJ operates on Eastern time; the cycle's published times are always Eastern. */
const EASTERN = 'America/New_York';

/**
 * Whether the portal is open at `now`.
 *
 * **Fails OPEN** on a missing or unparseable timestamp. A typo in one environment
 * file should not be able to lock every applicant out of a live cycle with no way
 * to recover but a redeploy; showing the portal early is the recoverable direction.
 */
export const isPortalOpen = (opensAt: string | undefined, now: Date = new Date()): boolean => {
	const opens = Date.parse(opensAt ?? '');
	if (Number.isNaN(opens)) return true;
	return now.getTime() >= opens;
};

/**
 * Whether the submission deadline has passed at `now` — the closing edge.
 *
 * Exactly complementary to the server's `isWithinSubmissionWindow`, which treats the
 * upper bound as EXCLUSIVE (`t < submissionDeadline`). So closed is `t >= deadline`,
 * and the two cannot disagree about the boundary instant.
 *
 * **Fails OPEN** — an absent or unparseable deadline reads as "not closed", matching
 * {@link isPortalOpen} and the config service's stated fallback policy. A cycle with
 * no deadline configured must not lock applicants out.
 *
 * This decides what the UI OFFERS. It is not enforcement: `evaluateSaveAuthorization`
 * refuses the write and returns `WINDOW_CLOSED` regardless of what this says.
 *
 * Deliberately answers only the question "may someone START an application", which is
 * the one question the client can answer as well as the server. For an application
 * that already exists the answer depends on per-application state the client does not
 * own — the HP→SLIP-Law-Clerk transfer re-opens editing AFTER this deadline — so that
 * case follows the server's `flags.canEdit` instead. Do not reuse this for it.
 */
export const isSubmissionClosed = (deadlineAt: string | undefined, now: Date = new Date()): boolean => {
	const deadline = Date.parse(deadlineAt ?? '');
	if (Number.isNaN(deadline)) return false;
	return now.getTime() >= deadline;
};

/**
 * A cycle timestamp as applicant-facing copy, always rendered in Eastern time
 * regardless of where the visitor's clock is set — e.g.
 * `August 28, 2026 at 10:00 AM Eastern`, or with `weekday: true`,
 * `Friday, August 28, 2026 at 10:00 AM Eastern`.
 *
 * Eastern rather than the visitor's local zone, because every DOJ-published date for
 * these programs is stated in Eastern. A Pacific applicant shown "8:59 PM" with no
 * zone would be reading a number that contradicts the vacancy announcement and the
 * landing page, and would have no way to tell which was wrong.
 *
 * Uses `Intl` rather than Angular's `date` pipe on purpose: the pipe's timezone
 * parameter takes a fixed offset, so a hardcoded `-0400` would silently render an
 * hour off once the date crossed a daylight-saving boundary.
 *
 * Returns an empty string for an unparseable timestamp, which pairs with the
 * fail-open behaviour above: callers render nothing rather than a broken date.
 */
export const easternMomentLabel = (isoTimestamp: string | undefined, options: { weekday?: boolean } = {}): string => {
	const at = Date.parse(isoTimestamp ?? '');
	if (Number.isNaN(at)) return '';

	const moment = new Date(at);
	const day = new Intl.DateTimeFormat('en-US', {
		timeZone: EASTERN,
		...(options.weekday ? { weekday: 'long' as const } : {}),
		month: 'long',
		day: 'numeric',
		year: 'numeric',
	}).format(moment);
	const time = new Intl.DateTimeFormat('en-US', { timeZone: EASTERN, hour: 'numeric', minute: '2-digit' }).format(moment);

	return `${day} at ${time} Eastern`;
};

/**
 * The opening moment as applicant-facing copy. Named for its call sites, which read
 * better as "opens" than as a generic moment; the formatting itself is shared with
 * the deadline so the two can never be presented differently.
 */
export const portalOpensLabel = (opensAt: string | undefined, options: { weekday?: boolean } = {}): string => easternMomentLabel(opensAt, options);

/**
 * The configured portal window, for components and guards.
 *
 * The opening timestamp comes from the server's public config document, not from the
 * build — see `OasisConfigService`. That is why the functions above take the
 * timestamp as an argument: swapping the source did not reach past this class.
 *
 * Injectable so it can be stubbed in tests. Callers evaluate `isOpen()` when they
 * render, so a visitor sitting on the page across the opening moment needs a refresh
 * to see the change — acceptable for a once-a-year event, and the alternative is a
 * polling timer.
 */
@Injectable({ providedIn: 'root' })
export class PortalAvailabilityService {
	private readonly config = inject(OasisConfigService);

	/** Whether applicants may sign in or create an account right now. */
	isOpen(now?: Date): boolean {
		return isPortalOpen(this.config.submissionOpens(), now);
	}

	/**
	 * Whether the submission deadline has passed — i.e. whether a NEW application may
	 * still be started.
	 *
	 * Only valid for that question. An application that already exists may still be
	 * editable after this deadline (the HP→SLIP-Law-Clerk transfer), and that answer is
	 * the server's `flags.canEdit`, not this.
	 */
	isSubmissionClosed(now?: Date): boolean {
		return isSubmissionClosed(this.config.submissionDeadline(), now);
	}

	/** The deadline as copy: `September 8, 2026 at 11:59 PM Eastern`. Empty if unset. */
	deadlineLabel(): string {
		return easternMomentLabel(this.config.submissionDeadline());
	}

	/** Long form, with weekday, for prose that leads with the date. */
	deadlineLabelWithWeekday(): string {
		return easternMomentLabel(this.config.submissionDeadline(), { weekday: true });
	}

	/**
	 * End of the HP→SLIP-Law-Clerk transfer window, as copy. Empty when the cycle
	 * configures no transfer window — the template then omits the sentence rather than
	 * promising an option that does not exist.
	 */
	programChangeDeadlineLabel(): string {
		return easternMomentLabel(this.config.programChangeDeadline());
	}

	/** Short form, for the nav bar: `August 28, 2026 at 10:00 AM Eastern`. */
	opensLabel(): string {
		return portalOpensLabel(this.config.submissionOpens());
	}

	/** Long form, for page copy: `Friday, August 28, 2026 at 10:00 AM Eastern`. */
	opensLabelWithWeekday(): string {
		return portalOpensLabel(this.config.submissionOpens(), { weekday: true });
	}
}
